import React from 'react';
import { Badge } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import OrganizationPatch from '../../components/OrganizationPatch';
import Avatar from '../../components/Avatar';
import JsonDisplay from '../../components/JsonDisplay';
import SeverityBadge from '../../components/SeverityBadge';
import ChangeDiff from '../../components/ChangeDiff';
import RelatedLogsTimeline from '../../components/RelatedLogsTimeline';
import RoleBadge from '../../components/RoleBadge';
import { fetchAuditLogWithRelated } from '../../slices/audit-logs.slice';
import { formatStatusValue } from '../../utils/formatStatus';

import './AuditLogDetailPanel.scss';

const OutcomeBadge = ({ outcome }) => {
    const config = {
        success: { color: 'success', icon: 'fa-check-circle' },
        failure: { color: 'danger', icon: 'fa-times-circle' },
        partial: { color: 'warning', icon: 'fa-exclamation-circle' }
    };
    const { color, icon } = config[outcome] || config.success;

    return (
        <Badge color={color} className={outcome === 'warning' ? 'text-dark' : ''} style={{ borderRadius: 0 }}>
            <span className={`fas ${icon} me-1`} />
            {outcome}
        </Badge>
    );
};

const MetadataCard = ({ icon, label, children }) => (
    <div className="metadata-card">
        <div className="text-white-50 small mb-1">
            <span className={`fas ${icon} me-1`} />
            {label}
        </div>
        <div>{children}</div>
    </div>
);

const AuditLogDetailPanel = ({ logId, onClose }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { selectedLog: auditLog, relatedLogs, selectedStatus } = useSelector(state => state.auditLogs);

    const handleSelectRelatedLog = (relatedLogId) => {
        navigate(`/audit/${relatedLogId}${window.location.search}`);
        dispatch(fetchAuditLogWithRelated(relatedLogId));
    };

    if (!auditLog && selectedStatus !== 'loading') {
        return null;
    }

    if (selectedStatus === 'loading') {
        return (
            <div className="audit-log-panel bg-broken shadow-lg">
                <div className="audit-log-panel-header">
                    <h5 className="mb-0">Loading...</h5>
                    <button className="btn btn-link text-white p-0" onClick={onClose}>
                        <span className="fas fa-times fa-lg" />
                    </button>
                </div>
                <div className="audit-log-panel-body d-flex align-items-center justify-content-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    const {
        _id,
        action, entity, details, createdAt,
        organization, user, ipAddress, userAgent,
        cronjob, payload,
        severity, targetId, targetModel, targetSnapshot,
        changes, outcome, duration,
        exportMeta, notificationMeta
    } = auditLog;

    return (
        <div className="audit-log-panel bg-broken shadow-lg">
            {/* Header */}
            <div className="audit-log-panel-header">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <SeverityBadge severity={severity || 'info'} />
                    <span className="text-white-50">[{entity?.toUpperCase()}]</span>
                    <span className="text-white">{action?.toLowerCase().replace(/_/g, ' ')}</span>
                </div>
                <button className="btn btn-link text-white p-0" onClick={onClose}>
                    <span className="fas fa-times fa-lg" />
                </button>
            </div>

            {/* Body */}
            <div className="audit-log-panel-body">
                {/* Details Banner */}
                <div className="details-banner mb-4">
                    <p className="mb-0">{details}</p>
                </div>

                {/* Metadata Grid */}
                <div className="metadata-grid mb-4">
                    <MetadataCard icon="fa-calendar" label="Date & Time">
                        {new Date(createdAt).toLocaleString()}
                    </MetadataCard>

                    {ipAddress && (
                        <MetadataCard icon="fa-network-wired" label="IP Address">
                            <code>{ipAddress}</code>
                        </MetadataCard>
                    )}

                    {userAgent && (
                        <MetadataCard icon="fa-globe" label="User Agent">
                            <span className="small text-truncate d-block" title={userAgent}>
                                {userAgent}
                            </span>
                        </MetadataCard>
                    )}

                    {outcome && outcome !== 'success' && (
                        <MetadataCard icon="fa-flag" label="Outcome">
                            <OutcomeBadge outcome={outcome} />
                        </MetadataCard>
                    )}

                    {duration && (
                        <MetadataCard icon="fa-stopwatch" label="Duration">
                            {duration >= 1000 ? `${(duration / 1000).toFixed(1)}s` : `${duration}ms`}
                        </MetadataCard>
                    )}

                    {cronjob && (
                        <MetadataCard icon="fa-clock" label="Source">
                            <Badge color="warning" className="text-dark" style={{ borderRadius: 0 }}>
                                <span className="fas fa-robot me-1" /> Cronjob
                            </Badge>
                        </MetadataCard>
                    )}
                </div>

                {/* User & Organization */}
                <div className="row mb-4">
                    {user && (
                        <div className="col-md-6 mb-3 mb-md-0">
                            <div className="text-white-50 small mb-2">Performed By</div>
                            <div className="d-flex align-items-center gap-3 p-2 bg-black/30 rounded">
                                <Avatar
                                    imageUrl={user.profilePic?.imageUrl}
                                    name={`${user.firstName} ${user.lastName}`}
                                    size={40}
                                />
                                <div>
                                    <div className="fw-bold">
                                        {user.firstName} {user.lastName}
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                        <code className="small text-white-50">@{user.username}</code>
                                        {user.role && <RoleBadge role={user.role} />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {organization && (
                        <div className="col-md-6">
                            <div className="text-white-50 small mb-2">Organization</div>
                            <OrganizationPatch organization={organization} />
                        </div>
                    )}
                </div>

                {/* Target Resource */}
                {targetId && targetSnapshot && (
                    <div className="mb-4">
                        <div className="text-white-50 small mb-2">
                            <span className="fas fa-bullseye me-1" />
                            Target Resource
                        </div>
                        <div className="p-3 bg-black/30 rounded border-start border-info border-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <Badge color="info" style={{ borderRadius: 0 }}>{targetModel}</Badge>
                                <code className="small text-white-50">{targetId}</code>
                            </div>
                            <div className="small">
                                {Object.entries(targetSnapshot).filter(([key]) => key !== '_id').map(([key, value]) => (
                                    <div key={key} className="d-flex gap-2">
                                        <span className="text-white-50">{key}:</span>
                                        <span>{formatStatusValue(key, value) || String(value)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Changes */}
                {changes && Object.keys(changes).length > 0 && (
                    <div className="mb-4">
                        <div className="text-white-50 small mb-2">
                            <span className="fas fa-exchange-alt me-1" />
                            Changes
                        </div>
                        <ChangeDiff changes={changes} />
                    </div>
                )}

                {/* Export Metadata */}
                {exportMeta && (
                    <div className="mb-4">
                        <div className="text-white-50 small mb-2">
                            <span className="fas fa-file-export me-1" />
                            Export Details
                        </div>
                        <div className="p-3 bg-black/30 rounded">
                            <div className="d-flex gap-3 flex-wrap">
                                <div>
                                    <span className="text-white-50">Format:</span>{' '}
                                    <Badge color="secondary" style={{ borderRadius: 0 }}>
                                        {exportMeta.format?.toUpperCase()}
                                    </Badge>
                                </div>
                                <div>
                                    <span className="text-white-50">Rows:</span>{' '}
                                    <strong>{exportMeta.rowCount}</strong>
                                </div>
                            </div>
                            {exportMeta.fields && (
                                <div className="mt-2">
                                    <span className="text-white-50">Fields:</span>{' '}
                                    <span className="small">{exportMeta.fields.join(', ')}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Notification Metadata */}
                {notificationMeta && (
                    <div className="mb-4">
                        <div className="text-white-50 small mb-2">
                            <span className="fas fa-bell me-1" />
                            Notification Details
                        </div>
                        <div className="p-3 bg-black/30 rounded">
                            <div className="d-flex gap-3 flex-wrap">
                                <div>
                                    <span className="text-white-50">Channel:</span>{' '}
                                    <Badge
                                        color={notificationMeta.channel === 'email' ? 'info' : 'purple'}
                                        className={notificationMeta.channel === 'slack' ? 'bg-purple' : ''}
                                        style={{ borderRadius: 0 }}
                                    >
                                        <span className={`fas fa-${notificationMeta.channel === 'email' ? 'envelope' : 'hashtag'} me-1`} />
                                        {notificationMeta.channel}
                                    </Badge>
                                </div>
                                {notificationMeta.subject && (
                                    <div>
                                        <span className="text-white-50">Subject:</span>{' '}
                                        <span>{notificationMeta.subject}</span>
                                    </div>
                                )}
                            </div>
                            {notificationMeta.recipients?.length > 0 && (
                                <div className="mt-2">
                                    <span className="text-white-50">Recipients:</span>{' '}
                                    <span className="small">{notificationMeta.recipients.join(', ')}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Payload */}
                {payload && Object.keys(payload).length > 0 && (
                    <div className="mb-4">
                        <JsonDisplay
                            data={payload}
                            title="Payload Data"
                            defaultOpen={false}
                        />
                    </div>
                )}

                {/* Related Activity Timeline */}
                <div className="related-section">
                    <div className="related-section-header">
                        <span className="fas fa-stream me-2" />
                        Related Activity
                        <span className="badge bg-secondary ms-2">
                            {(relatedLogs?.sameResource?.length || 0) +
                             (relatedLogs?.sameSession?.length || 0) +
                             (relatedLogs?.impersonationChain?.length || 0)}
                        </span>
                    </div>
                    <div className="related-section-legend">
                        <span className="legend-item resource">
                            <span className="legend-dot" /> Same Resource
                        </span>
                        <span className="legend-item session">
                            <span className="legend-dot" /> Same Session
                        </span>
                        <span className="legend-item impersonation">
                            <span className="legend-dot" /> Impersonation
                        </span>
                    </div>
                    <RelatedLogsTimeline
                        relatedLogs={relatedLogs}
                        currentLogId={_id}
                        onSelectLog={handleSelectRelatedLog}
                    />
                </div>
            </div>
        </div>
    );
};

export default AuditLogDetailPanel;
